import React from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useAuth, isElevated } from "@/contexts/AuthContext";
import { FLAG_PASSAGGIO, haFlag, vedeImpostazioni } from "@/lib/ambito";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Building2, Users, HardDrive, ListChecks, FolderKanban, ShieldCheck, ArrowRight, Settings as SettingsIcon, UserCheck, Briefcase, FileSignature, Database,
} from "lucide-react";
import LegacySettings from "@/pages/Settings";

function Tile({ to, icon: Icon, title, desc, testid }) {
  return (
    <Link to={to} className="block group" data-testid={testid}>
      <Card className="p-5 border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all bg-white h-full">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-semibold text-slate-900">{title}</div>
            <div className="text-sm text-slate-500 mt-0.5">{desc}</div>
          </div>
          <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-blue-600 transition-colors mt-1" />
        </div>
      </Card>
    </Link>
  );
}

export default function SettingsHub() {
  const { user } = useAuth();
  const [params] = useSearchParams();

  // If a ?tab= query is present (used for legacy tabs), render the old Settings UI
  const legacyTab = params.get("tab");
  // Sprint UI-GLOBALE — Aziende e Utenti hanno pagine dedicate: i vecchi indirizzi vi rimandano
  if (legacyTab === "companies") return <Navigate to="/companies" replace />;
  if (legacyTab === "users") return <Navigate to="/users" replace />;
  if (legacyTab && ["categories", "integrity"].includes(legacyTab)) {
    return <LegacySettings />;
  }

  const elevated = isElevated(user);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
          <SettingsIcon className="h-6 w-6 text-blue-600" /> Impostazioni
        </h1>
        <p className="text-sm text-slate-500 mt-1">Configurazione di KARGEST: anagrafica, utenti, permessi, backup.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {elevated && (
          <Tile to="/companies" icon={Building2} title="Aziende" desc="Anagrafica aziende, sedi e dati fiscali." testid="settings-tile-companies" />
        )}
        {elevated && (
          <Tile to="/users" icon={Users} title="Utenti" desc="Gestione utenti, ruoli e permessi azienda × sede." testid="settings-tile-users" />
        )}
        {elevated && (
          <Tile to="/settings?tab=categories" icon={FolderKanban} title="Categorie documenti" desc="Categorie e sottocategorie documenti." testid="settings-tile-categories" />
        )}
        {elevated && (
          <Tile to="/settings?tab=integrity" icon={ShieldCheck} title="Integrità archivio" desc="Verifica sincronia tra app e Dropbox." testid="settings-tile-integrity" />
        )}
        {elevated && (
          <Tile to="/admin/migration-review" icon={UserCheck} title="Revisione migrazione" desc="Utenti migrati dallo Sprint 0 da revisionare." testid="settings-tile-migration" />
        )}
        {elevated && (
          <Tile to="/admin/backup" icon={HardDrive} title="Backup" desc="Backup automatici e ripristino." testid="settings-tile-backup" />
        )}
        {elevated && (
          <Tile to="/admin/audit-log" icon={ListChecks} title="Audit log" desc="Registro delle azioni di sistema." testid="settings-tile-audit" />
        )}
        {(elevated || haFlag(user, FLAG_PASSAGGIO)) && (
          <Tile to="/settings/passaggio-proprieta" icon={FileSignature} title="Passaggio di proprietà" desc="Configura IPT, costi fissi e competenze per categoria veicolo." testid="settings-tile-passaggio" />
        )}
        {elevated && (
          <Tile to="/settings/elenchi-database" icon={Database} title="Elenchi database" desc="Province ISTAT e Codici IVA: tabelle di lookup globali." testid="settings-tile-elenchi-db" />
        )}
      </div>

      {!vedeImpostazioni(user) && (
        <Card className="p-6 text-sm text-slate-500 text-center">
          Nessuna impostazione disponibile per il tuo ruolo.
        </Card>
      )}
    </div>
  );
}
